import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { evidenceArtifactsKey, evidenceCheckKey } from './evidence-query-keys';
import type {
  EvidenceCheckStatus,
  EvidenceLibraryStatus,
  EvidenceLocationStatus,
  ProvisioningRun,
  ProvisioningRunDetail,
  StartProvisioningResult,
  ProvisionOutcome,
} from '@/api/types/evidence-provisioning.types';

// ── EVIDENCE P1.2 — folder provisioning ─────────────────────────────────────────────────────────
//
// A run is ASYNC: starting one returns immediately with its id, and the run record is the report. The
// run query polls only while the run is unfinished, so a finished run costs nothing to keep open.

const FINISHED = new Set(['completed', 'completed_with_failures']);
const POLL_MS = 3_000;

export function useStartProvisioningRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { controlReferences: string[]; scope?: 'bulk' | 'single' }) => {
      const { data } = await apiClient.post('/compliance/evidence/provisioning-runs', v);
      return data.data as StartProvisioningResult;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['evidence-provisioning', 'runs'] });
      for (const ref of v.controlReferences) {
        qc.invalidateQueries({ queryKey: ['evidence-provisioning', 'location', ref] });
      }
    },
  });
}

export function useProvisioningRun(runId: string | undefined) {
  return useQuery({
    queryKey: ['evidence-provisioning', 'run', runId],
    enabled: Boolean(runId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/evidence/provisioning-runs/${runId}`);
      return data.data as ProvisioningRunDetail;
    },
    // ⚠ Poll while the run can still change; stop the moment it cannot.
    refetchInterval: (query) => {
      const state = (query.state.data as ProvisioningRunDetail | undefined)?.run.state;
      return state && FINISHED.has(state) ? false : POLL_MS;
    },
  });
}

export function useProvisioningRuns() {
  return useQuery({
    queryKey: ['evidence-provisioning', 'runs'],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/evidence/provisioning-runs');
      return data.data as ProvisioningRun[];
    },
  });
}

export function useEvidenceLocation(reference: string | undefined) {
  return useQuery({
    queryKey: ['evidence-provisioning', 'location', reference],
    enabled: Boolean(reference),
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/evidence/locations/${reference}`);
      return data.data as EvidenceLocationStatus;
    },
  });
}

export function isFinishedRun(state: string): boolean {
  return FINISHED.has(state);
}

// ── P1.2 onboarding ─────────────────────────────────────────────────────────────────────────────

export function useEvidenceLibraryStatus() {
  return useQuery({
    queryKey: ['evidence-provisioning', 'library'],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/evidence/library');
      return data.data as EvidenceLibraryStatus;
    },
    // Poll only while a connect is in flight.
    refetchInterval: (query) =>
      (query.state.data as EvidenceLibraryStatus | undefined)?.state === 'connecting' ? POLL_MS : false,
  });
}

export function useConnectEvidenceLibrary() {
  const qc = useQueryClient();
  return useMutation({
    /** ⚠ Resolved identifiers only — the api refuses any other field, and never accepts a credential. */
    mutationFn: async (v: {
      entraTenantId: string; siteId: string; driveId: string; listId: string; libraryName: string; libraryWebUrl: string;
    }) => {
      const { data } = await apiClient.post('/compliance/evidence/library', v);
      return data.data as { integration_id: string; run_id: string; state: 'connecting' };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['evidence-provisioning', 'library'] }),
  });
}

/**
 * Create (or retry) ONE control's evidence folder — synchronously.
 *
 * ⚠ IT RETURNS THE TERMINAL STATE, NOT "STARTED". The endpoint runs the whole flow inline, so the
 * caller learns whether the folder exists rather than being handed a run id and left to discover
 * the outcome. A retry uses this same mutation: a retry is not a different operation, it is this
 * one against a row that already exists and failed.
 *
 * ⚠ THE LOCATION QUERY IS INVALIDATED ON EITHER OUTCOME. A `failed` result changes the row just as
 * much as a success does — refreshing only on success would leave the panel showing the state
 * before the attempt, which reads as "nothing happened".
 */
export function useProvisionControlEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { reference: string }) => {
      const { data } = await apiClient.post(
        `/compliance/evidence/controls/${encodeURIComponent(v.reference)}/provision`, {});
      return data.data as ProvisionOutcome;
    },
    onSettled: (_d, _e, v) => {
      qc.invalidateQueries({ queryKey: ['evidence-provisioning', 'location', v.reference] });
      // ⚠ B-EVIDENCE-ARTIFACTS-QUERYKEY-MISMATCH — the evidence list's key is
      //   ['evidence-artifacts', 'control', reference] (useControlEvidenceArtifacts). The old prefix
      //   ['evidence-artifacts', reference] matched NOTHING, so the list never refreshed.
      qc.invalidateQueries({ queryKey: evidenceArtifactsKey(v.reference) });
    },
  });
}

// ── Evidence monitoring S3 — the manual "Check for Evidence Updates" ─────────────────────────────────

const CHECK_PENDING = new Set(['queued', 'running']);

/**
 * The latest manual check of a location. ⚠ Polls ONLY while it is queued or running, then stops — and on
 * the transition from pending to finished, refreshes the location and the evidence list ONCE, because a
 * finished check changes what the panel shows.
 */
export function useEvidenceCheckStatus(reference: string | undefined, enabled = true) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: evidenceCheckKey(reference),
    enabled: Boolean(reference) && enabled,
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/compliance/evidence/locations/${encodeURIComponent(reference!)}/check-updates`);
      return data.data as EvidenceCheckStatus;
    },
    refetchInterval: (q) => {
      const state = (q.state.data as EvidenceCheckStatus | undefined)?.state;
      return state && CHECK_PENDING.has(state) ? POLL_MS : false;
    },
  });
  const state = query.data?.state;
  const prev = useRef(state);
  useEffect(() => {
    if (reference && prev.current && CHECK_PENDING.has(prev.current) && !(state && CHECK_PENDING.has(state))) {
      qc.invalidateQueries({ queryKey: ['evidence-provisioning', 'location', reference] });
      qc.invalidateQueries({ queryKey: evidenceArtifactsKey(reference) });
    }
    prev.current = state;
  }, [state, reference, qc]);
  return query;
}

/** Ask VeriGRC to check ONE location's folder for evidence updates now. The server fixes everything else. */
export function useRequestEvidenceCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { reference: string }) => {
      const { data } = await apiClient.post(
        `/compliance/evidence/locations/${encodeURIComponent(v.reference)}/check-updates`, {});
      return data.data as EvidenceCheckStatus & { check_id: string };
    },
    onSettled: (_d, _e, v) => {
      qc.invalidateQueries({ queryKey: evidenceCheckKey(v.reference) });
    },
  });
}
